import React from 'react';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { StarIcon } from '@heroicons/react/24/solid';
import { StarIcon as StarOutlineIcon } from '@heroicons/react/24/outline';

interface Review {
  id: string;
  guest: {
    name: string;
    image: string;
  };
  rating: number;
  comment: string;
  propertyName: string;
  date: Date;
}

const mockReviews: Review[] = [
  {
    id: '1',
    guest: {
      name: 'Sophie Martin',
      image: 'https://picsum.photos/200/200?random=7'
    },
    rating: 5,
    comment: 'Absolutely wonderful stay! The property exceeded our expectations in every way. The host was incredibly attentive and the amenities were top-notch.',
    propertyName: 'Luxury Villa',
    date: new Date(2024, 3, 10)
  },
  {
    id: '2',
    guest: {
      name: 'Robert Chen',
      image: 'https://picsum.photos/200/200?random=8'
    },
    rating: 4,
    comment: 'Great location and beautiful property. Very clean and well-maintained. Would definitely recommend!',
    propertyName: 'Beachfront Suite',
    date: new Date(2024, 3, 8)
  },
  {
    id: '3',
    guest: {
      name: 'Emily Wilson',
      image: 'https://picsum.photos/200/200?random=9'
    },
    rating: 5,
    comment: 'Perfect getaway spot! The attention to detail was amazing and the host made us feel very welcome.',
    propertyName: 'Mountain Cabin',
    date: new Date(2024, 3, 5)
  }
];

const ReviewsWidget: React.FC = () => {
  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }).map((_, index) => (
      index < rating ? (
        <StarIcon key={index} className="h-5 w-5 text-yellow-400" />
      ) : (
        <StarOutlineIcon key={index} className="h-5 w-5 text-gray-300" />
      )
    ));
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="bg-white rounded-2xl shadow-lg p-6"
    >
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Latest Reviews</h2>
          <p className="text-sm text-gray-500 mt-1">Recent feedback from your guests</p>
        </div>
        <div className="flex items-center bg-gray-50 px-4 py-2 rounded-xl">
          <span className="text-2xl font-bold text-gray-900 mr-2">4.8</span>
          <div className="flex items-center gap-1">
            {renderStars(5)}
          </div>
        </div>
      </div>

      <div className="space-y-6">
        {mockReviews.map((review, index) => (
          <motion.div
            key={review.id}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3, delay: index * 0.1 }}
            className="bg-gray-50 rounded-xl p-4 hover:bg-gray-100 transition-all duration-200 cursor-pointer group"
          >
            <div className="flex items-start space-x-4">
              <img
                src={review.guest.image}
                alt={review.guest.name}
                className="w-12 h-12 rounded-full object-cover border-2 border-white shadow-sm"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-sm font-medium text-gray-900 group-hover:text-airbnb-red transition-colors">
                      {review.guest.name}
                    </h3>
                    <p className="text-sm text-gray-500">{review.propertyName}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    {renderStars(review.rating)}
                  </div>
                </div>
                <p className="mt-2 text-sm text-gray-600 line-clamp-2">{review.comment}</p>
                <p className="mt-2 text-xs text-gray-500">
                  {format(review.date, 'MMMM d, yyyy')}
                </p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="mt-6 flex items-center justify-center">
        <button className="bg-gray-50 hover:bg-gray-100 text-gray-600 font-medium px-6 py-3 rounded-xl transition-colors flex items-center gap-2">
          <StarIcon className="h-5 w-5 text-yellow-400" />
          View All Reviews
        </button>
      </div>
    </motion.div>
  );
};

export default ReviewsWidget;